import {
  VISUAL_GAME_NPC_STATE_PATCH_FIELDS,
  VISUAL_GAME_OBJECT_STATE_PATCH_FIELDS,
  VISUAL_GAME_STATE_SCALAR_VALUE_TYPES,
} from '@drama/contracts/visual-game'

export const MODEL_DELTA_STATE_REGISTRY_WRITERS = Object.freeze({
  playerKp: 'KP delta',
  npcActionKp: 'NPC action KP delta',
})

const MODEL_DELTA_WRITERS = new Set(Object.values(MODEL_DELTA_STATE_REGISTRY_WRITERS))
const STATE_SCALAR_VALUE_TYPES = new Set(VISUAL_GAME_STATE_SCALAR_VALUE_TYPES)
const PATCH_FIELD_DEFINITIONS = Object.freeze([
  ...Object.entries(VISUAL_GAME_OBJECT_STATE_PATCH_FIELDS),
  ...Object.entries(VISUAL_GAME_NPC_STATE_PATCH_FIELDS),
])

/**
 * Fail compilation when a registry entry advertised to a model cannot be written
 * through CandidateDelta's strict schema.
 *
 * @param {Array<Record<string, any>>} stateRegistry
 */
export function assertModelWritableStateRegistry(stateRegistry) {
  for (const [index, entry] of stateRegistry.entries()) {
    if (!isModelWritableEntry(entry)) continue
    if (isCandidateDeltaRegistryEntry(entry)) continue
    const id = typeof entry.id === 'string' && entry.id.trim()
      ? entry.id
      : `stateRegistry.entries[${index}]`
    throw new Error(
      `compilePack: model-writable stateRegistry entry cannot be expressed by CandidateDelta: ${id}`,
    )
  }
}

/** @param {Record<string, any>} entry */
function isModelWritableEntry(entry) {
  return Boolean(entry)
    && typeof entry === 'object'
    && !Array.isArray(entry)
    && Array.isArray(entry.writers)
    && entry.writers.some(writer => MODEL_DELTA_WRITERS.has(writer))
}

/** @param {Record<string, any>} entry */
function isCandidateDeltaRegistryEntry(entry) {
  if (typeof entry.id !== 'string' || typeof entry.scope !== 'string') return false
  const path = entry.id.split('.')
  if (path.some(segment => !segment)) return false
  switch (entry.scope) {
    case 'global_flag':
      return path.length === 1 && STATE_SCALAR_VALUE_TYPES.has(entry.valueType)
    case 'stat':
      return path.length === 1 && entry.valueType === 'number'
    case 'object_state':
    case 'npc_state':
    case 'npc_relationship_flag':
      return PATCH_FIELD_DEFINITIONS.some(([field, definition]) => (
        definition.registryScope === entry.scope
        && registryPathMatches(path, field, definition.registryPathKind)
        && definition.registryValueTypes.includes(entry.valueType)
      ))
    default:
      return false
  }
}

function registryPathMatches(path, field, pathKind) {
  switch (pathKind) {
    case 'field':
      return path.length === 2 && path[1] === field
    case 'array_item':
    case 'record_entry':
      return path.length === 3 && path[1] === field
    default:
      throw new Error(`Unhandled CandidateDelta registry path kind: ${pathKind}`)
  }
}
